import { Link, NavLink, Navigate, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  UserRound,
  Calendar,
  MessageSquare,
  Star,
  LogOut,
} from "lucide-react";
import { useSession } from "@/hooks/useSession";

const LINKS = [
  { to: "/account", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/account/profile", label: "Profile", icon: UserRound },
  { to: "/account/bookings", label: "Bookings", icon: Calendar },
  { to: "/account/messages", label: "Messages", icon: MessageSquare },
  { to: "/account/reviews", label: "Reviews", icon: Star },
];

export const AccountLayout = () => {
  const { user, isAuthenticated, checking, logout } = useSession();
  const navigate = useNavigate();

  if (checking) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login?redirect=/account" replace />;
  }

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <main className="mx-auto max-w-[1400px] px-5 pb-24 pt-10 md:grid md:grid-cols-[240px_1fr] md:gap-16 md:px-10 md:pt-16">
      {/* Sidebar */}
      <aside className="mb-8 md:mb-0" data-testid="account-sidebar">
        <div className="flex items-center gap-3 border-b border-foreground/10 pb-6">
          <img
            src={user.avatar}
            alt=""
            className="h-11 w-11 rounded-full object-cover"
          />
          <div className="min-w-0">
            <p className="truncate font-display text-lg tracking-tight text-foreground">
              {user.name}
            </p>
            <p className="truncate text-xs text-muted-foreground">
              {user.email}
            </p>
          </div>
        </div>

        <nav className="mt-6 flex gap-1 overflow-x-auto no-scrollbar md:mt-8 md:flex-col md:gap-0.5 md:overflow-visible">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`account-nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `flex shrink-0 items-center gap-3 whitespace-nowrap rounded-full px-4 py-2.5 text-sm transition-colors duration-200 md:rounded-none md:border-l-2 md:px-3 ${
                  isActive
                    ? "bg-foreground text-background md:border-primary md:bg-transparent md:text-foreground"
                    : "text-muted-foreground hover:text-foreground md:border-transparent"
                }`
              }
            >
              <l.icon size={14} />
              {l.label}
            </NavLink>
          ))}
          <button
            type="button"
            data-testid="account-logout"
            onClick={onLogout}
            className="ml-1 inline-flex shrink-0 items-center gap-3 whitespace-nowrap rounded-full px-4 py-2.5 text-sm text-muted-foreground transition-colors hover:text-primary md:ml-0 md:mt-6 md:rounded-none md:border-l-2 md:border-transparent md:px-3 md:pt-6"
          >
            <LogOut size={14} /> Log out
          </button>
        </nav>

        <Link
          to="/properties"
          className="mt-8 hidden rounded-full bg-primary px-4 py-2.5 text-center text-xs font-medium text-primary-foreground md:block"
        >
          Explore new stays
        </Link>
      </aside>

      {/* Main content */}
      <section className="min-w-0">
        <Outlet />
      </section>
    </main>
  );
};

export default AccountLayout;
